/**
 * The ids a delete just removed, held between the service that deleted them and
 * the saga that purges their side data (VE-28551).
 *
 * `PURGE_TDO_SIDE_DATA` exists because the folder/case delete happens inside a
 * SERVICE thunk, which cannot call a saga — so a plain action became the bridge.
 * That made a hard delete across four registries (redact state, status history,
 * media comments, document comments) reachable by dispatch, from anywhere,
 * against any id: nothing in the payload proves the file was deleted, and SDO
 * writes are authorized at schema level, so the server will not refuse. A
 * view-only reviewer could have read a tdoId off a row and erased that file's
 * redaction boxes, its comment thread and its whole provenance trail — including
 * the soft-delete tombstones Clear deliberately preserves — while the file
 * itself carried on existing.
 *
 * So the ids do not travel in the payload alone. The deleting service registers
 * what the SERVER confirmed it deleted; the watcher purges only what it can
 * claim from that register, once. A dispatch from anywhere else claims nothing
 * and is dropped.
 *
 * This is a client-side integrity check, not an authorization control — the same
 * user can call `deleteStructuredData` directly with their own token. What it
 * buys is that the app itself offers no such button, matching the control the
 * statusHistory module already applies to the lesser operation (see the role and
 * read-verification re-checks in `onClearStatusHistory`).
 */

/**
 * Bounded so a dispatch that never arrives — a watcher not yet forked, a tab
 * closing mid-delete — cannot grow this without limit. Far past any real
 * request: the largest observed delete is a few thousand files.
 */
const MAX_PENDING = 10000;

const pending = new Set<string>();

/** Register ids the server confirmed deleted, just before dispatching. */
export const markTdosPurgeable = (tdoIds: readonly string[]): void => {
  tdoIds.forEach((tdoId) => {
    if (!tdoId) {
      return;
    }
    if (pending.size >= MAX_PENDING) {
      // Drop the oldest rather than refuse the newest: a stale id purges
      // nothing, while refusing the newest strands a file just deleted.
      const oldest = pending.values().next().value;
      if (oldest !== undefined) {
        pending.delete(oldest);
      }
    }
    pending.add(String(tdoId));
  });
};

/**
 * Take back the ids that were registered, removing them — so one registration
 * authorizes exactly one purge, and a replayed action purges nothing.
 */
export const claimPurgeableTdos = (tdoIds: readonly string[]): string[] =>
  tdoIds.filter((tdoId) => pending.delete(String(tdoId)));

/** Test seam. */
export const resetPurgeableTdos = (): void => pending.clear();
